import { screenSmallerThan } from '../shared/utilities/screenSize';
import BaseBehavior from './base/base';

interface SliderOptions {
    classNameFrame?: string;
    classNameSlideContainer?: string;
    enableMouseEvents?: boolean;
    infinite?: boolean | number;
}

interface SliderConstructorParams {
    dotContainer: string;
    slideContainer: string;
    sliderOpts?: SliderOptions;
    element: HTMLElement;
    createBullets?: boolean;
    isTabFocusDisabled?: boolean;
}
type disableTabFocusType = () => void;
type eventListenersType = () => void;
type addBulletsType = () => void;
type addBulletsEventListenersType = () => void;
type onBulletClickType = (event:Event) => void;
type resetBulletsType = () => void;
type setActiveBulletType = ({ detail }:{ detail?: { currentSlide: number } }) => void;
type initLoryType = (slideContainer: HTMLElement | null) => Promise<any> | null;
type destroyLoryType = () => void;

export default class Slider extends BaseBehavior {
    protected dotContainer: HTMLElement | null;
    private createBullets: boolean;
    protected slideContainer: HTMLElement | null;
	protected dotCount: number;
    private bullets: (HTMLLIElement | undefined)[];
    protected slider: any;
    protected sliderOpts: SliderOptions;
    private loading: Promise<any> | null;

    constructor({
		dotContainer,
		slideContainer,
		sliderOpts,
		element,
		createBullets = false,
		isTabFocusDisabled = false
	}: SliderConstructorParams) {
        const hiddenCardsOnMobile =
			screenSmallerThan('sm') && element.querySelector('.v2-card-container')
				? element.querySelectorAll('.v2-show-sm.v2-slider__slide')
				: null;

		if (hiddenCardsOnMobile) {
			hiddenCardsOnMobile.forEach(item => item.remove());
		}
        super(element);

        this.dotContainer = element.querySelector(dotContainer);
        this.createBullets = createBullets;
        this.sliderOpts = {
			classNameFrame: 'v2-slider__frame',
			classNameSlideContainer: 'v2-slider__slides',
			enableMouseEvents: true,
			...sliderOpts
		};
        if (isTabFocusDisabled) {
			this.disableTabFocus();
		}
        this.slideContainer = element.querySelector(slideContainer);
		this.dotCount =
			(element.querySelectorAll('.v2-slider__slides .v2-slider__slide') || []).length ||
			(element.querySelectorAll('.v2-slider__slides li') || []).length;
        this.bullets = [];
        this.eventListeners();
    }

    disableTabFocus: disableTabFocusType = ():void => {
		Array.from(this.element.querySelectorAll('a')).forEach((el:HTMLAnchorElement) => {
			el.setAttribute('tabindex', '-1');
		});
	}

    eventListeners: eventListenersType = ():void =>{
		if (this.createBullets && this.dotCount > 1) {
			this.element.addEventListener('before.lory.init', this.addBullets.bind(this));
			this.element.addEventListener('after.lory.init', this.addBulletsEventListeners.bind(this));
			this.element.addEventListener('after.lory.slide', this.setActiveBullet.bind(this));
		}
		this.element.addEventListener('after.lory.destroy', () => {
			this.slider = undefined;
		});
	}

    addBullets: addBulletsType = ():void =>{
        this.bullets = [...Array(this.dotCount)].map(() =>
			this.dotContainer?.appendChild(document.createElement('li'))
		);
		if (this.bullets.length) {
			this.bullets[0]?.classList.add('active');
		}
    }

    addBulletsEventListeners: addBulletsEventListenersType = (): void =>{
		this.bullets.forEach(bullet => {
			bullet?.addEventListener('click', e => this.onBulletClick(e));
		});
	}

    onBulletClick: onBulletClickType = ({ target }): void => {
		const targetIdx = this.bullets.indexOf(target as HTMLLIElement);
		this.slider.slideTo(targetIdx);
	}

	resetBullets: resetBulletsType = ():void => {
		this.setActiveBullet({});
	}

	setActiveBullet: setActiveBulletType = ({ detail = { currentSlide: 0 } } = {}):void => {
		this.bullets.forEach((bullet, idx) => {
			if (
				this.sliderOpts.infinite ? detail.currentSlide - 1 === idx : detail.currentSlide === idx
			) {
				bullet?.classList.add('active');
			} else {
				bullet?.classList.remove('active');
			}
		});
	}

    initLory:initLoryType = (slideContainer) => {
		if (!this.loading) {
			this.loading = import(/* webpackChunkName: "lory" */ 'lory.js')
				.then(({ lory }) => {
					this.loading = null;
					this.slider = lory(slideContainer, this.sliderOpts);
					return this.slider;
				})
				.catch(e => console.error(e));
		}		
		return this.loading;
	}

    destroyLory:destroyLoryType = (): void =>{
		this.slider.destroy();
		this.element
			.querySelector('.' + this.sliderOpts.classNameSlideContainer)
			.removeAttribute('style');
	}

}

